/**
 * The TxnScreen ↔ shell contract for Open editors — delivered through context
 * so no host has to thread new props through its object renderers.
 *
 * Two roles:
 *
 *   'main'     the screen the reader is looking at. Its Add / Edit / deep-link
 *              opens are offered to the shell (`requestEditor`); when the
 *              shell takes one, the screen does NOT open its own editor — a
 *              dedicated session instance does.
 *   'session'  one kept-alive TxnScreen per open editor. It opens from the
 *              session's intent, shows only its record editor, keeps that
 *              editor MOUNTED while minimised (so typed values, child buffers,
 *              lookups and item_change results survive) and reports back.
 *
 * A screen with no provider above it (designer preview, an embed, a drawer —
 * `ObjectDrawer` resets the context to null) behaves exactly as before.
 */
import { createContext, useContext, type ReactNode } from 'react'
import type { EditorIntent, EditorMode, EditorSession, EditorSnapshot } from './editorSessions'

export interface ShellEditorMain {
  role: 'main'
  objName: string
  /** Offer an open to the shell. True = the shell took it (do not open locally). */
  requestEditor(intent: EditorIntent): boolean
  /** The session currently shown for THIS screen (for URL reflection), or null. */
  activeEditor: { mode: EditorMode; txnId?: string } | null
  /** Changes after any session of this screen saved — refresh the list. */
  refreshToken: number
}

export interface SessionReport {
  dirty?: boolean
  title?: string
  mode?: EditorMode
  txnId?: string
}

export interface ShellEditorSession {
  role: 'session'
  objName: string
  session: EditorSession
  /** Whether this session's editor is the one on screen. */
  visible: boolean
  /** The lease the screen asks for when it takes the record's edit lock. */
  lockTtlSeconds: number
  /** Bumped when the reader closes this editor from a list (×): run the
   *  editor's own close (which asks Save / Discard when there is work). */
  closeRequest: number
  report(patch: SessionReport): void
  /** The reader pressed Minimise. */
  minimise(): void
  /** The editor closed (saved and closed, or discarded) — the session ends. */
  closed(reason: 'saved' | 'discarded'): void
  /** A save went through (the editor may stay open) — lists refresh. */
  saved(): void
  /** Reload-recovery copy; the shell persists it (debounced). */
  snapshot(snap: EditorSnapshot): void
}

export type ShellEditorValue = ShellEditorMain | ShellEditorSession

const ShellEditorContext = createContext<ShellEditorValue | null>(null)

export function ShellEditorProvider({ value, children }: { value: ShellEditorValue | null; children: ReactNode }) {
  return <ShellEditorContext.Provider value={value}>{children}</ShellEditorContext.Provider>
}

/** The shell's editor contract for THIS object, or null (behave as before). */
export function useShellEditor(objName: string): ShellEditorValue | null {
  const v = useContext(ShellEditorContext)
  if (!v || !objName) return null
  return v.objName === objName ? v : null
}
